import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, CalendarDays, ExternalLink, Gift, Home, LogIn, LogOut, Menu, Sparkles, Swords, Ticket, Trophy, User, UserCircle, X } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { getViewerSession } from "../lib/viewerSession";
import "../public-site.css";

export const PUBLIC_BRAND_IMAGE = "/the-barracks-rat-icon.png";

export const PUBLIC_LINKS = {
  discord: "https://discord.gg/aJANnHSJ5V",
  kick: "https://kick.com/thebarracksrat",
  twitch: "https://www.twitch.tv/thebarracksrat",
  youtube: "https://www.youtube.com/@TheBarracksRat",
};

const baseNavItems = [
  { to: "/", label: "Home", icon: Home },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/events", label: "Events", icon: Sparkles },
  { to: "/redeems", label: "Redeems", icon: Ticket },
  { to: "/rpg", label: "RPG", icon: Swords },
  { to: "/giveaways", label: "Giveaways", icon: Gift },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

export default function PublicSiteLayout({ children }) {
  const [session, setSession] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef(null);
  const menuPanel = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1280px)");
    const closeOnDesktop = () => {
      if (media.matches) setMenuOpen(false);
    };
    media.addEventListener("change", closeOnDesktop);
    return () => media.removeEventListener("change", closeOnDesktop);
  }, []);

  const closeMenuOnEscape = (event) => {
    if (event.key === "Escape" && menuOpen) {
      setMenuOpen(false);
      menuButton.current?.focus();
    }
  };

  const loadSession = useCallback(async () => {
    const { session } = await getViewerSession();
    setSession(session);
  }, []);

  useEffect(() => {
    loadSession();
    window.addEventListener("viewer-profile-updated", loadSession);
    return () => window.removeEventListener("viewer-profile-updated", loadSession);
  }, [loadSession]);

  const navItems = session
    ? [...baseNavItems, { to: "/profile", label: "Profile", icon: UserCircle }]
    : [...baseNavItems, { to: "/login", label: "Login", icon: LogIn }];

  return (
    <div className="public-site-theme public-site-shell min-h-screen">
      <div className="public-site-vignette" />
      <a className="public-skip-link" href="#public-main">Skip to content</a>
      <div className="public-frame">
        <header className="public-header" onKeyDown={closeMenuOnEscape}>
          <Link to="/" className="public-brand">
            <img
              src={PUBLIC_BRAND_IMAGE}
              alt=""
              width="40"
              height="40"
              className="w-10 h-10 rounded object-cover"
            />
            <div>
              <div className="public-brand-name">The Barracks Rat</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Parker  -  streams, videos, chaos</div>
            </div>
          </Link>

          <button ref={menuButton} type="button" className="public-menu-toggle" aria-expanded={menuOpen} aria-controls="public-navigation" onClick={() => setMenuOpen((open) => !open)}>
            {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
            <span>{menuOpen ? "Close" : "Menu"}</span>
          </button>
          <div ref={menuPanel} id="public-navigation" className={`public-navigation${menuOpen ? " is-open" : ""}`} onBlur={(event) => {
            if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget) && !menuButton.current?.contains(event.relatedTarget)) setMenuOpen(false);
          }}>
            <nav className="public-nav" aria-label="Public website navigation">
              {navItems.map((item) => (
                <PublicNavLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  active={isPublicNavActive(item.to, location)}
                  onClick={() => {
                    if (menuOpen) {
                      setMenuOpen(false);
                    }
                  }}
                >
                  {item.label}
                </PublicNavLink>
              ))}
            </nav>
            <div className="shrink-0">
              <PublicSessionChip session={session} />
            </div>
          </div>
        </header>

        <main id="public-main" className="public-main">{children}</main>

        <footer className="public-footer">
          <div>Home of The Ratterday Saints. Not a cult. Probably.</div>
          <div className="flex flex-wrap gap-3">
            <a href={PUBLIC_LINKS.twitch} target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1">
              Twitch <ExternalLink className="w-3 h-3" />
            </a>
            <a href={PUBLIC_LINKS.kick} target="_blank" rel="noopener noreferrer" className="hover:text-white">Kick</a>
            <a href={PUBLIC_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white">YouTube</a>
            <a href={PUBLIC_LINKS.discord} target="_blank" rel="noopener noreferrer" className="hover:text-white">Discord</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function PublicNavLink({ to, active, icon: Icon, children, onClick }) {
  const classes = `public-nav-link${active ? " is-active" : ""}`;
  return (
    <Link to={to} className={classes} aria-current={active ? "page" : undefined} onClick={onClick}>
      {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />}
      <span>{children}</span>
    </Link>
  );
}

function isPublicNavActive(to, location) {
  const [path, hash = ""] = to.split("#");
  const currentPath = location.pathname === "/dungeons" ? "/rpg" : location.pathname;
  if (currentPath !== path && !(path !== "/" && currentPath.startsWith(`${path}/`))) return false;
  if (hash) return location.hash === `#${hash}`;
  return true;
}

function PublicSessionChip({ session }) {
  if (!session) return null;
  const displayName = session.display_name || session.login || "Profile";
  const profileImageUrl = resolvePublicMediaUrl(session.profile_image_url);
  const signOut = async () => {
    try { await api.post("/auth/logout"); } catch (err) {}
    window.location.href = "/login";
  };
  return (
    <div className="public-session-chip inline-flex items-stretch w-fit max-w-full rounded bg-white/[0.035]">
      <Link
        to="/profile"
        className="inline-flex items-center gap-2 min-w-0 px-2.5 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-white/[0.04] transition"
        data-testid="public-session-profile-link"
      >
        {profileImageUrl ? (
          <img src={profileImageUrl} alt="" className="w-7 h-7 rounded object-cover border border-white/35" />
        ) : (
          <span className="w-7 h-7 rounded bg-white/[0.06] inline-flex items-center justify-center shrink-0">
            <User className="w-4 h-4" />
          </span>
        )}
        <span className="min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Signed in</span>
          <span className="block truncate max-w-[8rem]">{displayName}</span>
        </span>
      </Link>
      <button
        type="button"
        onClick={signOut}
        className="px-2.5 border-l border-white/10 text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/[0.04] transition"
        aria-label="Sign out"
        title="Sign out"
        data-testid="public-sign-out"
      >
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
}

function resolvePublicMediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${BACKEND_URL}${raw}`;
  return raw;
}

export function PageHero({ eyebrow, title, body, actions }) {
  return (
    <section className="public-page-hero">
      <div className="label-eyebrow mb-3 text-[hsl(var(--currency-event))]">{eyebrow}</div>
      <h1>{title}</h1>
      <p className="mt-4 text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</p>
      {actions && <div className="public-actions mt-6">{actions}</div>}
    </section>
  );
}

export function PublicCard({ icon: Icon, title, body, href }) {
  const content = <>
      {Icon && (
        <span className="public-icon mb-3">
          <Icon className="w-5 h-5" strokeWidth={1.55} />
        </span>
      )}
      <h3 className="font-semibold mb-1 text-[hsl(var(--foreground))]">{title}{href && <ArrowUpRight className="public-card-arrow" size={16} aria-hidden="true" />}</h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</p>
    </>;
  if (href?.startsWith("http")) return <a className="public-card public-card-link" href={href} target="_blank" rel="noopener noreferrer">{content}</a>;
  if (href) return <Link className="public-card public-card-link" to={href}>{content}</Link>;
  return <div className="public-card">{content}</div>;
}

export function PublicLinkButton({ href, children, primary = false }) {
  const classes = primary
    ? "public-link-button public-link-button-primary inline-flex items-center gap-2 px-5 py-3 rounded text-white font-medium transition"
    : "public-link-button public-link-button-secondary inline-flex items-center gap-2 py-2 text-[hsl(var(--muted-foreground))] font-medium transition";

  if (href?.startsWith("http")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes}>
        {children}{!primary && <ArrowUpRight className="public-link-arrow" size={15} aria-hidden="true" />}
      </a>
    );
  }

  return (
    <Link to={href} className={classes}>
      {children}{!primary && <ArrowUpRight className="public-link-arrow" size={15} aria-hidden="true" />}
    </Link>
  );
}
